import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ROLE_LABELS } from '../lib/constants';
import ProfessionalLayout from '../components/ProfessionalLayout';
import '../styles/professional-theme.css';
import { Users, Plus, Trash2, Edit2, MapPin, Building2, Save, X, CheckCircle, AlertCircle, Pencil, Upload } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import { createUserSchema, editUserSchema, validateForm } from '../lib/validation';

const UsersManagement = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const roleLabels = (ROLE_LABELS[language] || ROLE_LABELS.fr);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [headquarters, setHeadquarters] = useState([]);
  const [deliveryCountries, setDeliveryCountries] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'client',
    phone: '',
    country: '',
    city: '',
    assigned_country: '',
    country_role: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = React.useRef(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    role: '',
    country: '',
    city: '',
    assigned_country: '',
    country_role: '',
    is_active: true
  });

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchUsers();
    fetchSettings();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(t('errorLoadingUsers'));
    } finally {
      setLoading(false);
    }
  };

  const handleImportClients = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de re-sélectionner le même fichier
    if (!file) return;
    setError(null);
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      // Pas de Content-Type manuel : le navigateur pose le boundary multipart.
      const response = await fetch(`${apiUrl}/api/clients/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'import');
      }
      const parts = [`${data.imported} client(s) importé(s)`];
      if (data.skipped) parts.push(`${data.skipped} ignoré(s)`);
      if (data.errors?.length) parts.push(`${data.errors.length} erreur(s)`);
      setSuccess(parts.join(' · '));
      setTimeout(() => setSuccess(false), 6000);
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/company/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHeadquarters(data.general?.headquarters || []);
        setDeliveryCountries(data.general?.delivery_countries || []);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormErrors({});

    const validation = validateForm(createUserSchema, formData);
    if (!validation.success) {
      setFormErrors(validation.errors);
      return;
    }

    try {
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/users/create-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          country_role: formData.country_role || null,
          assigned_country: formData.assigned_country || null,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData.detail;
        const msg = typeof detail === 'string' ? detail
          : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
          : t('errorCreatingUser');
        throw new Error(msg);
      }

      setSuccess(true);
      setShowAddModal(false);
      setFormErrors({});
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'client',
        phone: '',
        country: '',
        city: '',
        assigned_country: '',
        country_role: ''
      });
      
      await fetchUsers();
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = (userId) => {
    const targetUser = users.find(u => u.id === userId);
    setUserToDelete(targetUser);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchUsers();
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(t('errorDeletingUser'));
    } finally {
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setEditFormData({
      name: u.name || '',
      phone: u.phone || '',
      role: u.role || '',
      country: u.country || '',
      city: u.city || '',
      assigned_country: u.assigned_country || '',
      country_role: u.country_role || '',
      is_active: u.is_active !== false
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setEditFormErrors({});

    const validation = validateForm(editUserSchema, editFormData);
    if (!validation.success) {
      setEditFormErrors(validation.errors);
      return;
    }

    try {
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...editFormData,
          country_role: editFormData.country_role || null,
          assigned_country: editFormData.assigned_country || null,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData.detail;
        const msg = typeof detail === 'string' ? detail
          : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
          : t('errorEditingUser');
        throw new Error(msg);
      }

      setSuccess(true);
      setShowEditModal(false);
      setEditingUser(null);
      await fetchUsers();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const getRoleLabel = (role) => {
    return roleLabels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      'platform_admin': '#dc2626',
      'super_admin': '#1a1a1a',
      'company_admin': '#e8590c',
      'country_admin': '#666666',
      'partner': '#b0b0b0',
      'local_partner': '#b0b0b0',
      'client': '#999999'
    };
    return colors[role] || '#999999';
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div className="skeleton" style={{ width: '100%', height: '400px' }}></div>
        </div>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
      {/* Header */}
      <div style={{ marginBottom: 'var(--spacing-6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--spacing-4)' }}>
          <div>
            <h1 className="heading-1" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <Users size={40} style={{ color: 'var(--accent)' }} />
              {t('usersManagement')}
            </h1>
            <p className="body-base" style={{ marginTop: 'var(--spacing-2)', color: 'hsl(var(--muted-foreground))' }}>
              {users.length} utilisateur{users.length > 1 ? 's' : ''} dans votre entreprise
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
            <input
              ref={importInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleImportClients}
            />
            <button
              onClick={() => importInputRef.current?.click()}
              className="btn-secondary-pro"
              disabled={importing}
              title="Importer des clients depuis un fichier CSV ou Excel (colonnes : Nom, Téléphone ; Email/Pays/Ville optionnels)"
            >
              <Upload size={18} />
              {importing ? 'Import…' : 'Importer les clients'}
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-accent-pro"
            >
              <Plus size={18} />
              {t('newUser')}
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {success && (
        <div style={{
          padding: 'var(--spacing-4)',
          background: 'var(--success-50)',
          border: '1px solid var(--success)',
          borderRadius: 'var(--radius)',
          color: 'var(--success-700)',
          marginBottom: 'var(--spacing-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)'
        }}>
          <CheckCircle size={20} />
          {typeof success === 'string' ? success : t('userCreatedSuccess')}
        </div>
      )}

      {error && (
        <div style={{
          padding: 'var(--spacing-4)',
          background: 'var(--danger-50)',
          border: '1px solid var(--danger)',
          borderRadius: 'var(--radius)',
          color: 'var(--danger-700)',
          marginBottom: 'var(--spacing-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)'
        }}>
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Liste des utilisateurs */}
      {users.length === 0 ? (
        <EmptyState
          icon={Users}
          title={t('noUsers')}
          description="Créez votre premier utilisateur pour commencer"
          actionLabel="Créer un utilisateur"
          onAction={() => setShowAddModal(true)}
        />
      ) : (
      <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
        {users.map((u) => (
          <div
            key={u.id}
            className="card-professional"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--spacing-4)',
              borderLeft: `4px solid ${getRoleColor(u.role)}`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)', flex: 1 }}>
              {/* Avatar */}
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'var(--accent-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--accent)',
                fontSize: 'var(--text-xl)',
                fontWeight: 'var(--font-bold)'
              }}>
                {u.name ? u.name.charAt(0).toUpperCase() : u.email.charAt(0).toUpperCase()}
              </div>

              {/* Infos */}
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: '4px' }}>
                  {u.name || u.email}
                </h3>
                <p style={{ fontSize: 'var(--text-sm)', color: 'hsl(var(--muted-foreground))', marginBottom: '8px' }}>
                  {u.email}
                </p>
                
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Badge Rôle */}
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 'var(--font-bold)',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius)',
                    background: getRoleColor(u.role),
                    color: 'white'
                  }}>
                    {getRoleLabel(u.role)}
                  </span>

                  {/* Badge Pays Assigné */}
                  {u.assigned_country && (
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 'var(--font-bold)',
                      padding: '4px 8px',
                      borderRadius: 'var(--radius)',
                      background: u.country_role === 'headquarter' ? 'var(--level-2)' : 
                                 u.country_role === 'delivery' ? 'var(--level-4)' : 
                                 'var(--accent)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      {u.country_role === 'headquarter' && '🏠'}
                      {u.country_role === 'delivery' && '🎯'}
                      {u.country_role === 'both' && '🌍'}
                      {u.assigned_country}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              {(user?.role === 'company_admin' || user?.role === 'super_admin') && u.id !== user?.id && (
                <>
                  <button
                    onClick={() => handleEditUser(u)}
                    style={{
                      padding: 'var(--spacing-2)',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--accent)',
                      cursor: 'pointer',
                      borderRadius: 'var(--radius)'
                    }}
                    title={t('edit')}
                  >
                    <Pencil size={18} />
                  </button>
                  {u.role !== 'company_admin' && (
                    <button
                      onClick={() => handleDeleteUser(u.id)}
                      style={{
                        padding: 'var(--spacing-2)',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--danger)',
                        cursor: 'pointer',
                        borderRadius: 'var(--radius)'
                      }}
                      title={t('delete')}
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Dialog de confirmation suppression */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('deleteUser')}
        description={`Êtes-vous sûr de vouloir supprimer ${userToDelete?.name || userToDelete?.email} ? Cette action est irréversible.`}
        confirmLabel={t('delete')}
        onConfirm={confirmDeleteUser}
        variant="destructive"
      />

      {/* Modal Création Utilisateur */}
      {showAddModal && (
        <div
          onClick={() => setShowAddModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--spacing-4)'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card-professional"
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <h2 className="heading-2" style={{ marginBottom: 'var(--spacing-6)' }}>
              {t('createUser')}
            </h2>

            <form onSubmit={handleCreateUser} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
              {/* Nom */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  marginBottom: 'var(--spacing-2)'
                }}>
                  {t('fullName')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder={t('fullNamePlaceholder')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${formErrors.name ? '#EF4444' : 'hsl(var(--border))'}`,
                    borderRadius: 'var(--radius)',
                    fontSize: 'var(--text-base)'
                  }}
                />
                {formErrors.name && <span style={{ color: '#EF4444', fontSize: 'var(--text-xs)', marginTop: '4px', display: 'block' }}>{formErrors.name}</span>}
              </div>

              {/* Email */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  marginBottom: 'var(--spacing-2)'
                }}>
                  {t('email')} *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder={t('emailPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${formErrors.email ? '#EF4444' : 'hsl(var(--border))'}`,
                    borderRadius: 'var(--radius)',
                    fontSize: 'var(--text-base)'
                  }}
                />
                {formErrors.email && <span style={{ color: '#EF4444', fontSize: 'var(--text-xs)', marginTop: '4px', display: 'block' }}>{formErrors.email}</span>}
              </div>

              {/* Mot de passe */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  marginBottom: 'var(--spacing-2)'
                }}>
                  {t('password')} *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  placeholder={t('minCharsPassword')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${formErrors.password ? '#EF4444' : 'hsl(var(--border))'}`,
                    borderRadius: 'var(--radius)',
                    fontSize: 'var(--text-base)'
                  }}
                />
                {formErrors.password && <span style={{ color: '#EF4444', fontSize: 'var(--text-xs)', marginTop: '4px', display: 'block' }}>{formErrors.password}</span>}
              </div>

              {/* Téléphone */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  marginBottom: 'var(--spacing-2)'
                }}>
                  {t('phone')}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder={t('phonePlaceholder')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid hsl(var(--border))',
                    borderRadius: 'var(--radius)',
                    fontSize: 'var(--text-base)'
                  }}
                />
              </div>

              {/* Rôle */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  marginBottom: 'var(--spacing-2)'
                }}>
                  {t('role')} *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid hsl(var(--border))',
                    borderRadius: 'var(--radius)',
                    fontSize: 'var(--text-base)',
                    background: 'white'
                  }}
                >
                  <option value="client">{roleLabels.client}</option>
                  <option value="partner">{roleLabels.partner}</option>
                  <option value="local_partner">{roleLabels.local_partner}</option>
                  <option value="country_admin">{roleLabels.country_admin}</option>
                  {['platform_admin', 'super_admin'].includes(user?.role) && <option value="company_admin">{roleLabels.company_admin}</option>}
                </select>
              </div>

              {/* Pays / Ville (si partner ou local_partner) */}
              {['partner', 'local_partner'].includes(formData.role) && (
                <>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-semibold)',
                      marginBottom: 'var(--spacing-2)'
                    }}>
                      {t('country')}
                    </label>
                    <select
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value, city: '' })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid hsl(var(--border))',
                        borderRadius: 'var(--radius)',
                        fontSize: 'var(--text-base)',
                        background: 'white'
                      }}
                    >
                      <option value="">-- {t('selectCountry')} --</option>
                      <optgroup label={t('headquartersShipping')}>
                        {headquarters.map((hq, idx) => (
                          <option key={`hq-${idx}`} value={hq.name || hq.country_name}>
                            {hq.name || hq.country_name} ({hq.code || hq.country_code})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label={t('deliveryCountries')}>
                        {deliveryCountries.map((country, idx) => (
                          <option key={`delivery-${idx}`} value={country.name || country.country_name}>
                            {country.name || country.country_name} ({country.code || country.country_code})
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {formData.country && (
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-semibold)',
                        marginBottom: 'var(--spacing-2)'
                      }}>
                        {t('city')}
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder={language === 'fr' ? 'Ville du partenaire' : 'Partner city'}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid hsl(var(--border))',
                          borderRadius: 'var(--radius)',
                          fontSize: 'var(--text-base)'
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Assignation Pays (si country_admin) */}
              {formData.role === 'country_admin' && (
                <>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-semibold)',
                      marginBottom: 'var(--spacing-2)'
                    }}>
                      {t('assignedCountry')} *
                    </label>
                    <select
                      value={formData.assigned_country}
                      onChange={(e) => setFormData({ ...formData, assigned_country: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid var(--accent)',
                        borderRadius: 'var(--radius)',
                        fontSize: 'var(--text-base)',
                        background: 'white'
                      }}
                    >
                      <option value="">-- {t('selectCountry')} --</option>
                      <optgroup label={t('headquartersShipping')}>
                        {headquarters.map((hq, idx) => (
                          <option key={`hq-${idx}`} value={hq.name || hq.country_name}>
                            {hq.name || hq.country_name} ({hq.code || hq.country_code})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label={t('deliveryCountries')}>
                        {deliveryCountries.map((country, idx) => (
                          <option key={`delivery-${idx}`} value={country.name || country.country_name}>
                            {country.name || country.country_name} ({country.code || country.country_code})
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-semibold)',
                      marginBottom: 'var(--spacing-2)'
                    }}>
                      {t('type')} *
                    </label>
                    <select
                      value={formData.country_role}
                      onChange={(e) => setFormData({ ...formData, country_role: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid var(--accent)',
                        borderRadius: 'var(--radius)',
                        fontSize: 'var(--text-base)',
                        background: 'white'
                      }}
                    >
                      <option value="">-- {t('selectType')} --</option>
                      <option value="headquarter">{t('headquartersShipping')}</option>
                      <option value="delivery">{t('delivery')}</option>
                      <option value="both">{t('both')}</option>
                    </select>
                  </div>
                </>
              )}

              {/* Boutons */}
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-4)' }}>
                <button
                  type="submit"
                  className="btn-accent-pro"
                  style={{ flex: 1 }}
                >
                  <Save size={18} />
                  {t('createUser')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary-pro"
                  style={{ flex: 1 }}
                >
                  <X size={18} />
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Édition Utilisateur */}
      {showEditModal && editingUser && (
        <div
          onClick={() => setShowEditModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--spacing-4)'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card-professional"
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <h2 className="heading-2" style={{ marginBottom: 'var(--spacing-6)' }}>
              {t('editUser')}
            </h2>
            <p className="body-small" style={{ marginBottom: 'var(--spacing-4)', color: 'hsl(var(--muted-foreground))' }}>
              {editingUser.email}
            </p>

            <form onSubmit={handleSaveEdit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                  {t('fullName')} *
                </label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px', border: `2px solid ${editFormErrors.name ? '#EF4444' : 'hsl(var(--border))'}`, borderRadius: 'var(--radius)', fontSize: 'var(--text-base)' }}
                />
                {editFormErrors.name && <span style={{ color: '#EF4444', fontSize: 'var(--text-xs)', marginTop: '4px', display: 'block' }}>{editFormErrors.name}</span>}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                  {t('phone')}
                </label>
                <input
                  type="tel"
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '2px solid hsl(var(--border))', borderRadius: 'var(--radius)', fontSize: 'var(--text-base)' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                  {t('role')} *
                </label>
                <select
                  value={editFormData.role}
                  onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px', border: '2px solid hsl(var(--border))', borderRadius: 'var(--radius)', fontSize: 'var(--text-base)', background: 'white' }}
                >
                  <option value="client">{roleLabels.client}</option>
                  <option value="partner">{roleLabels.partner}</option>
                  <option value="local_partner">{roleLabels.local_partner}</option>
                  <option value="country_admin">{roleLabels.country_admin}</option>
                  {['platform_admin', 'super_admin'].includes(user?.role) && <option value="company_admin">{roleLabels.company_admin}</option>}
                </select>
              </div>

              {['partner', 'local_partner'].includes(editFormData.role) && (
                <>
                  <div>
                    <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                      {t('country')}
                    </label>
                    <select
                      value={editFormData.country}
                      onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value, city: '' })}
                      style={{ width: '100%', padding: '12px', border: '2px solid hsl(var(--border))', borderRadius: 'var(--radius)', fontSize: 'var(--text-base)', background: 'white' }}
                    >
                      <option value="">-- {t('selectCountry')} --</option>
                      <optgroup label={t('headquartersShipping')}>
                        {headquarters.map((hq, idx) => (
                          <option key={`edit-hq-${idx}`} value={hq.name || hq.country_name}>
                            {hq.name || hq.country_name} ({hq.code || hq.country_code})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label={t('deliveryCountries')}>
                        {deliveryCountries.map((country, idx) => (
                          <option key={`edit-delivery-${idx}`} value={country.name || country.country_name}>
                            {country.name || country.country_name} ({country.code || country.country_code})
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {editFormData.country && (
                    <div>
                      <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                        {t('city')}
                      </label>
                      <input
                        type="text"
                        value={editFormData.city}
                        onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                        style={{ width: '100%', padding: '12px', border: '2px solid hsl(var(--border))', borderRadius: 'var(--radius)', fontSize: 'var(--text-base)' }}
                      />
                    </div>
                  )}
                </>
              )}

              {editFormData.role === 'country_admin' && (
                <>
                  <div>
                    <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                      {t('assignedCountry')}
                    </label>
                    <select
                      value={editFormData.assigned_country}
                      onChange={(e) => setEditFormData({ ...editFormData, assigned_country: e.target.value })}
                      style={{ width: '100%', padding: '12px', border: '2px solid var(--accent)', borderRadius: 'var(--radius)', fontSize: 'var(--text-base)', background: 'white' }}
                    >
                      <option value="">-- {t('selectCountry')} --</option>
                      <optgroup label={t('headquarters')}>
                        {headquarters.map((hq, idx) => (
                          <option key={`edit-hq-${idx}`} value={hq.name || hq.country_name}>
                            {hq.name || hq.country_name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label={t('deliveryCountries')}>
                        {deliveryCountries.map((country, idx) => (
                          <option key={`edit-delivery-${idx}`} value={country.name || country.country_name}>
                            {country.name || country.country_name}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                      {t('type')}
                    </label>
                    <select
                      value={editFormData.country_role}
                      onChange={(e) => setEditFormData({ ...editFormData, country_role: e.target.value })}
                      style={{ width: '100%', padding: '12px', border: '2px solid var(--accent)', borderRadius: 'var(--radius)', fontSize: 'var(--text-base)', background: 'white' }}
                    >
                      <option value="">-- {t('select')} --</option>
                      <option value="headquarter">{t('headquarters')}</option>
                      <option value="delivery">{t('delivery')}</option>
                      <option value="both">{t('both')}</option>
                    </select>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-4)' }}>
                <button type="submit" className="btn-accent-pro" style={{ flex: 1 }}>
                  <Save size={18} />
                  {t('save')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary-pro"
                  style={{ flex: 1 }}
                >
                  <X size={18} />
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </ProfessionalLayout>
  );
};

export default UsersManagement;
